import { Flex } from 'components';
import {
  NumberCell as NumberCellStyled,
  NumberBoxWrapper,
  IconsWrapper,
  NumberCellWrapper
} from './NumberCell.styled';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import {
  GradingScaleInformationRowWrapper,
  Numbers
} from '../../AssessmentModal.styled';
import { useTranslation } from 'react-i18next';
import { SquareNumberBox } from './SquareNumberBox/SquareNumberBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperclip,
  faPersonRunning
} from '@fortawesome/free-solid-svg-icons';
import { faComment } from '@fortawesome/free-regular-svg-icons';
import { NotesModal } from 'components/NotesModal/NotesModal';
import { IDetailedAssessment, IScore, TableScore } from 'types/assessments';
import { patchAssessmentScore, postAssessmentScore } from 'api/assessments';
import { toast } from 'react-toastify';
import { CriterionTaskModal } from './CriterionTasksModal/CriterionTasksModal';
import { IBlock } from 'types/methologies';
import { Item } from '../../AssessmentModal';

interface Props {
  bgColor: string;
  score: TableScore;
  setClickedCriterion: Dispatch<SetStateAction<Item | undefined>>;
  clickedCriterion?: Item;
  showTooltip?: boolean;
  fontSize: number;
  setShowCriteriaModal: React.Dispatch<React.SetStateAction<boolean>>;
  setCriteriaModalTitle: React.Dispatch<React.SetStateAction<string>>;
  cellHeight: number;
  criterion: Item;
  usedEvalations: IDetailedAssessment[];
  setUsedEvalations: React.Dispatch<
    React.SetStateAction<IDetailedAssessment[]>
  >;
  selectedBlock: IBlock;
}

export const NumberCell = ({
  bgColor,
  score,
  setClickedCriterion,
  clickedCriterion,
  showTooltip = false,
  fontSize,
  setShowCriteriaModal,
  setCriteriaModalTitle,
  cellHeight,
  criterion,
  usedEvalations,
  setUsedEvalations,
  selectedBlock
}: Props) => {
  const [isSmallerCell, setIsSmallerCell] = useState(false);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isCriterionTasksModalOpen, setIsCriterionTasksModalOpen] =
    useState(false);

  const { t } = useTranslation();

  const injectReceivedData = useCallback(
    (newScore: IScore, prevAssessments: IDetailedAssessment[]) => {
      const oldAssessment = prevAssessments.find(
        ({ id }) => id === score.assessmentId
      );

      const oldBlock = oldAssessment?.practice.blocks.find(
        (block) => block.id === selectedBlock?.id
      );

      const oldCriterion = oldBlock?.criteria.find(
        (criterion) => criterion.id === score.criterionId
      );

      if (oldAssessment && oldBlock && oldCriterion) {
        const newAssessments = prevAssessments.map((prevAssessment) =>
          prevAssessment.id !== score.assessmentId
            ? prevAssessment
            : {
                ...prevAssessment,
                practice: {
                  ...prevAssessment.practice,
                  blocks: prevAssessment.practice.blocks.map((prevBlock) =>
                    oldBlock.id !== prevBlock.id
                      ? prevBlock
                      : {
                          ...oldBlock,
                          criteria: oldBlock.criteria.map((prevCriterion) =>
                            oldCriterion.id !== prevCriterion.id
                              ? prevCriterion
                              : { ...oldCriterion, score: newScore }
                          )
                        }
                  )
                }
              }
        );

        return newAssessments;
      }
    },
    [score.criterionId, score.assessmentId, selectedBlock?.id]
  );

  const handleChangeCellValue = async (newValue: string) => {
    if (newValue !== score.value) {
      try {
        let newScore;

        if (score.value === undefined || score.value === null) {
          const body = {
            score: {
              score: Number(newValue),
              criterion_id: score.criterionId,
              assessment_id: score.assessmentId,
              area_id: score.areaId
            }
          };

          const { data } = await postAssessmentScore(score.assessmentId, body);

          if (data) {
            newScore = data;
          }
        } else if (score.id) {
          const body = {
            score: {
              score: Number(newValue)
            }
          };

          const { data } = await patchAssessmentScore(
            score.assessmentId,
            score.id,
            body
          );

          if (data) {
            newScore = data;
          }
        }

        if (newScore) {
          const newSelectedAssessments = injectReceivedData(
            newScore,
            usedEvalations
          );

          if (newSelectedAssessments) {
            setUsedEvalations(newSelectedAssessments);
          }
        }
      } catch (e) {
        toast.error(t('anErrorOccurred'));
      }
    }
  };

  return (
    <Flex
      flexDirection={'column'}
      onFocus={() => {
        setClickedCriterion(criterion);
        setIsSmallerCell(true);
      }}
      onBlur={() => {
        setClickedCriterion(undefined);
        setIsSmallerCell(false);
      }}
    >
      <NumberCellWrapper>
        <NumberCellStyled
          height={`${isSmallerCell ? cellHeight - 20 : cellHeight}px`}
          type="text"
          bgColor={bgColor}
          value={score.value}
          readOnly={true}
        />
        {clickedCriterion?.number === criterion.number && (
          <IconsWrapper cellHeight={cellHeight}>
            <FontAwesomeIcon
              icon={faPersonRunning}
              cursor="pointer"
              onMouseDown={() => setIsCriterionTasksModalOpen(true)}
            />
            <FontAwesomeIcon
              icon={faPaperclip}
              cursor="pointer"
              onMouseDown={() => {
                setShowCriteriaModal(true);
                setCriteriaModalTitle('attachmentsForCriterion');
              }}
            />
            <FontAwesomeIcon
              icon={faComment}
              cursor="pointer"
              onMouseDown={() => setIsCommentModalOpen(true)}
            />
          </IconsWrapper>
        )}
      </NumberCellWrapper>

      {clickedCriterion?.number === criterion.number && (
        <>
          <NumberBoxWrapper id="grading-scale">
            <SquareNumberBox
              color="green"
              value="3"
              setValue={handleChangeCellValue}
            />
            <SquareNumberBox
              color="blue"
              value="1"
              setValue={handleChangeCellValue}
            />
            <SquareNumberBox
              color="red"
              value="0"
              setValue={handleChangeCellValue}
            />
          </NumberBoxWrapper>
          {showTooltip && (
            <Tooltip
              style={{ width: 60 + (fontSize - 16) * 5 + '%', zIndex: '99' }}
              anchorId="grading-scale"
            >
              <GradingScaleInformationRowWrapper>
                <Numbers>3 -&nbsp;</Numbers>
                {t('assessmentsView.gradingScaleDescription.3')}
              </GradingScaleInformationRowWrapper>
              <GradingScaleInformationRowWrapper>
                <Numbers>1 -&nbsp;</Numbers>
                {t('assessmentsView.gradingScaleDescription.1')}
              </GradingScaleInformationRowWrapper>
              <GradingScaleInformationRowWrapper>
                <Numbers>0 -&nbsp;</Numbers>
                {t('assessmentsView.gradingScaleDescription.0')}
              </GradingScaleInformationRowWrapper>
            </Tooltip>
          )}
        </>
      )}
      <NotesModal
        isOpen={isCommentModalOpen}
        onCancelClick={() => setIsCommentModalOpen(false)}
        customTitle="tasks.commentTitle"
      />
      {isCriterionTasksModalOpen && (
        <CriterionTaskModal
          isOpen={isCriterionTasksModalOpen}
          onCancelClick={() => setIsCriterionTasksModalOpen(false)}
          score={score}
        />
      )}
    </Flex>
  );
};
